import { apiSlice } from './apiSlice';
import { ORDERS_URL } from '../constants';

export const orderApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // =====================================================
        // Create a new order
        // =====================================================
        createOrder: builder.mutation({
            query: (order) => ({
                url: ORDERS_URL,
                method: 'POST',
                body: order,
            }),
        }),
        // =====================================================
        // Get order details by ID
        // =====================================================
        getOrderDetails: builder.query({
            query: (id) => ({
                url: `${ORDERS_URL}/${id}`,
            }),
            keepUnusedDataFor: 5,
        }),
        // =====================================================
        // Get shipping label for order by ID
        // =====================================================
        getShippingLabel: builder.query({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/label`,
            }),
            keepUnusedDataFor: 5,
        }),
        // =====================================================
        // Get orders of the logged-in user
        // =====================================================
        getMyOrders: builder.query({
            query: () => ({
                url: `${ORDERS_URL}/mine`,
            }),
            keepUnusedDataFor: 5,
        }),
        // =====================================================
        // Admin: Get all orders
        // =====================================================
        getOrders: builder.query({
            query: () => ({
                url: ORDERS_URL,
            }),
            keepUnusedDataFor: 5,
        }),
        // =====================================================
        // Admin: Mark order as delivered
        // =====================================================
        deliverOrder: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/deliver`,
                method: 'PUT',
            }),
        }),
        // =====================================================
        // Admin: Mark order as paid
        // =====================================================
        markOrderPaid: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/pay`,
                method: 'PUT',
                body: {
                    isAdmin: true,
                },
            }),
            invalidatesTags: ['Order'],
        }),
        // =====================================================
        // Admin: Toggle accept item
        // =====================================================
        toggleAcceptItem: builder.mutation({
            query: ({
                orderId,
                itemId,
            }) => ({
                url:
                    `${ORDERS_URL}/${orderId}/toggle-accept`,
                method: 'PUT',
                body: {
                    itemId,
                },
            }),
        }),
        // =====================================================
        // Admin: Delete order item
        // =====================================================
        deleteOrderItem: builder.mutation({
            query: ({
                orderId,
                itemId,
            }) => ({
                url:
                    `${ORDERS_URL}/${orderId}/delete-item`,
                method: 'PUT',
                body: {
                    itemId,
                },
            }),
            invalidatesTags: ['Order'],
        }),
        // =====================================================
        // Admin: Undo removed item
        // =====================================================
        undoRemoveItem: builder.mutation({
            query: ({
                orderId,
                removedItemId,
            }) => ({
                url:
                    `${ORDERS_URL}/${orderId}/undo-item`,
                method: 'PUT',
                body: {
                    removedItemId,
                },
            }),
            invalidatesTags: ['Order'],
        }),
        // =====================================================
        // Admin: Get undelivered orders by user
        // =====================================================
        getUndeliveredOrdersByUser: builder.query({
            query: (userId) => ({
                url:
                    `${ORDERS_URL}/undelivered/user/${userId}`,
            }),
            keepUnusedDataFor: 5,
        }),
        // =====================================================
        // Admin: Accept order item
        // =====================================================
        acceptOrderItem: builder.mutation({
            query: ({
                orderId,
                itemId,
                accepted_price,
            }) => ({
                url:
                    `${ORDERS_URL}/${orderId}/items/${itemId}/accept`,
                method: 'PUT',
                body: {
                    accepted_price,
                },
            }),
        }),
        // =====================================================
        // DHL: Get shipping label information
        // =====================================================
        getShippingLabel: builder.query({
            query: (orderId) => ({
                url:
                    `${ORDERS_URL}/${orderId}/label`,
            }),
            keepUnusedDataFor: 5,
        }),
        // =====================================================
        // DHL: Download REAL shipping label PDF
        // =====================================================
        downloadShippingLabel: builder.query({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/label/download`,
                /*
                 * DHL returns a PDF, not JSON.
                 *
                 * Therefore RTK Query must convert the
                 * response into a Blob.
                 */
                responseHandler: async (response) => {
                    if (!response.ok) {
                        let errorData = {};
                        try {
                            errorData =
                                await response.json();
                        } catch {
                            // Response was not JSON.
                        }
                        throw new Error(
                            errorData.message ||
                            'Het DHL-verzendlabel kon niet worden geladen.'
                        );
                    }
                    return response.blob();
                },
            }),
            /*
             * Do not keep PDF blobs in RTK Query cache.
             */
            keepUnusedDataFor: 0,
        }),
    }),
    overrideExisting: false,
});

export const {
    useCreateOrderMutation,
    useGetOrderDetailsQuery,
    useGetMyOrdersQuery,
    useGetOrdersQuery,
    useDeliverOrderMutation,
    useMarkOrderPaidMutation,
    useToggleAcceptItemMutation,
    useDeleteOrderItemMutation,
    useUndoRemoveItemMutation,
    useGetUndeliveredOrdersByUserQuery,
    useAcceptOrderItemMutation,
    useGetShippingLabelQuery,
    useLazyDownloadShippingLabelQuery,
} = orderApiSlice;
